import { Input } from '@foodsby/nutrient'
import { Box, List, ListItem, ListItemText, Typography } from '@material-ui/core'
import { RadioButtonChecked, RadioButtonUnchecked } from '@material-ui/icons'
import React, { useState } from 'react'
import scriptLoader from 'react-async-script-loader'
import Dotdotdot from 'react-dotdotdot'
import PlacesAutocomplete from 'react-places-autocomplete'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { search } from '../../redux/modules/locationSearch'
import { selectCurrentLocationWithAddress } from '../../redux/modules/selectors'
import { updateAndSaveUserLocationStart } from '../../redux/modules/user'
import { locationRoute } from '../../routes/routes'
import { formatUrl } from '../../util/formatUtils'
import { formatSavedLocations } from '../../util/locationUtils'
import { MAPS_SCRIPT_URL } from '../location-search/LocationSearchBox'

const getHighlightedText = (text, highlight) => {
  const highlightParts = highlight.split(' ')
  const parts = text.split(' ')

  return (
    <span>
      {parts?.map((part, i) => (
        <span
          key={i}
          style={
            highlightParts?.some(
              highlight =>
                part
                  .replace(/[^a-z0-9]/gi, '')
                  .toLowerCase()
                  .indexOf(highlight?.toLowerCase().replace(/[^a-z0-9]/gi, '')) > -1,
            )
              ? { fontWeight: 'bold' }
              : {}
          }
        >
          {`${part} `}
        </span>
      ))}
    </span>
  )
}

const ListSavedLocations = ({
  defaultLocation,
  savedLocations,
  updateAndSaveUserLocationStart,
}) => {
  const history = useHistory()

  const goToLocation = locationId => {
    updateAndSaveUserLocationStart(locationId)
    history.push(formatUrl(locationRoute.path, { locationId: locationId }))
  }

  const formattedSavedLocations = formatSavedLocations(
    savedLocations,
    defaultLocation?.deliveryLocationId,
  )
  return (
    <List>
      {defaultLocation &&
        formattedSavedLocations?.length > 0 &&
        formattedSavedLocations?.map(location => {
          const isLocationDefault =
            location.deliveryLocationId === defaultLocation.deliveryLocationId
          return (
            <ListItem
              button
              key={location.deliveryLocationId}
              onClick={() => goToLocation(location.deliveryLocationId)}
            >
              <Box marginRight={3}>
                {isLocationDefault ? (
                  <RadioButtonChecked color="primary" />
                ) : (
                  <RadioButtonUnchecked />
                )}
              </Box>
              <ListItemText>
                <Dotdotdot clamp={1}>
                  <Typography color={isLocationDefault ? 'primary' : 'textPrimary'}>
                    {location.name || location.deliveryLocationName}
                  </Typography>
                </Dotdotdot>
                <Dotdotdot clamp={1}>
                  <Typography
                    color={isLocationDefault ? 'primary' : 'textSecondary'}
                    variant="body2"
                  >
                    {location.street}
                  </Typography>
                </Dotdotdot>
              </ListItemText>
            </ListItem>
          )
        })}
    </List>
  )
}

const LocationSearch = ({
  initialAddress = '',
  location,
  savedLocations,
  search,
  updateAndSaveUserLocationStart,
}) => {
  const [address, changeAddress] = useState(initialAddress)

  return (
    <>
      <PlacesAutocomplete
        googleCallbackName="loadGoogleMaps"
        highlightFirstSuggestion
        onChange={changeAddress}
        onError={(status, clearSuggestions) => {
          console.warn('Google Maps API returned error with status: ', status)
          clearSuggestions()
        }}
        onSelect={search}
        value={address}
      >
        {({ getInputProps, getSuggestionItemProps, suggestions }) => {
          const dropdownVisible = suggestions.length > 0
          return (
            <Box padding={3}>
              <StyledInput
                {...getInputProps({
                  autoFocus: true,
                  dropdownVisible,
                  id: 'searchbox',
                  onKeyDown: e => {
                    if (!suggestions.length && e.keyCode === 13) {
                      search(address)
                    }
                  },
                  placeholder: '123 Spaghetti Street',
                })}
              />
              <DropDown dropdownVisible={dropdownVisible}>
                {suggestions.map((suggestion, i) => {
                  const className = suggestion.active ? '-suggestion -active' : '-suggestion'
                  return (
                    <div
                      key={i}
                      {...getSuggestionItemProps(suggestion, {
                        className,
                      })}
                    >
                      <span>
                        {getHighlightedText(suggestion.description, getInputProps().value)}
                      </span>
                    </div>
                  )
                })}
                {suggestions?.length > 0 && (
                  <div className="autocomplete-suggestion google-attribute">
                    <img
                      alt="Powered by Google"
                      src="https://images.foodsby.com/third-party/powered_by_google_on_white.png"
                    />
                  </div>
                )}
              </DropDown>
            </Box>
          )
        }}
      </PlacesAutocomplete>
      <ListSavedLocations
        defaultLocation={location}
        savedLocations={savedLocations}
        updateAndSaveUserLocationStart={updateAndSaveUserLocationStart}
      />
    </>
  )
}

const StyledInput = styled(Input)`
  background: ${props => props.theme.color.white};
  border-bottom: solid 1px ${props => props.theme.color.greyShade1};
  border-radius: calc(${props => props.theme.baseUnit} * 5);
  border: solid 1px ${props => props.theme.color.greyShade1};
  color: ${props => props.theme.group1shade9};
  font-family: ${props => props.theme.fontFamily};
  font-size: calc(${props => props.theme.baseUnit} * 4);
  font-weight: bold;
  height: calc(${props => props.theme.baseUnit} * 8);
  width: 288px;

  ${props =>
    props.dropdownVisible &&
    css`
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    `}

  &::placeholder {
    font-weight: normal;
  }

  &:focus {
    background: ${props => props.theme.color.white};
    border: solid 1px ${props => props.theme.color.greyShade1};
    box-shadow: none;
  }
`

const DropDown = styled.div`
  background-color: ${props => props.theme.color.white};
  border-bottom-left-radius: calc(${props => props.theme.baseUnit} * 4);
  border-bottom-right-radius: calc(${props => props.theme.baseUnit} * 4);
  border: none;
  color: ${props => props.theme.group1shade9};
  font-family: ${props => props.theme.fontFamily};
  font-size: calc(${props => props.theme.baseUnit} * 4);
  position: absolute;
  top: calc(${props => props.theme.baseUnit} * 8 + 1);
  width: 288px;
  z-index: 1;
  overflow: hidden;

  ${props =>
    props.dropdownVisible &&
    css`
      border: solid 1px ${props => props.theme.color.greyShade1};
      border-top: none;
    `}

  & .-suggestion {
    padding: ${props => props.theme.baseUnit} calc(${props => props.theme.baseUnit} * 3);

    & .-active,
    &:hover {
      background-color: ${props => props.theme.color.brandPrimary};
      color: ${props => props.theme.color.white};
    }
  }
`

const matchStateToProps = state => {
  const { error, loading } = state.locationSearch
  const { savedLocations } = state.location
  const location = selectCurrentLocationWithAddress(state)

  return {
    error,
    loading,
    location,
    savedLocations,
  }
}

const matchDispatchToProps = { search, updateAndSaveUserLocationStart }

export default scriptLoader(MAPS_SCRIPT_URL)(
  connect(matchStateToProps, matchDispatchToProps)(LocationSearch),
)
